import React, { useCallback, useEffect, useState } from "react";
import callApi from "Common/utils/apiCaller";
import { showNotification } from "Common/utils/Notification";
import LoaderComponent from "Common/Components/Loader/Loader";
import { emitSourceChoose, emitSourceSaved, requestVerify } from "./sourceEvents";

// The Data source field under one ticker. A plain dropdown that is always there. Picking
// another source and pressing Save switches it. Solactive needs a login: if none is saved
// yet it is typed once in the "Verify source files" card, never here. All rules live in the API.

type Props = {
  indexCode?: string; // the saved ticker, e.g. IUSAAGN
  returnType: string; // PR, TR or NTR
};

const SOLACTIVE = "solective"; // the exact name the API's daily sync switches on

const IndexDataSource: React.FC<Props> = ({ indexCode, returnType }) => {
  const [config, setConfig] = useState<any>(null);
  const [choice, setChoice] = useState("ical3");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const applyConfig = useCallback((next: any) => {
    setConfig(next);
    setChoice(next?.data_source || "ical3");
  }, []);

  const load = useCallback(() => {
    if (!indexCode) return Promise.resolve();
    return callApi(`v1/index-datasource/by-index/${encodeURIComponent(indexCode)}`, "get")
      .then((res: any) => applyConfig(res?.data?.config))
      .catch((error: any) => {
        setLoadError(
          error?.status === 403
            ? "You do not have access to data source settings."
            : error?.message || "Could not load the data source."
        );
      });
  }, [indexCode, applyConfig]);

  useEffect(() => {
    setConfig(null);
    setLoadError("");
    if (!indexCode) return;
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [indexCode, load]);

  // The card saved a login (and with it Solactive): follow along.
  useEffect(() => {
    const onSaved = (e: any) => {
      if (e?.detail?.indexCode === indexCode) load();
    };
    window.addEventListener("indxx:source-saved", onSaved);
    return () => window.removeEventListener("indxx:source-saved", onSaved);
  }, [indexCode, load]);

  const sources: { value: string; label: string }[] = config?.sources || [];
  const savedSource = config?.data_source || "ical3";
  const changed = choice !== savedSource;
  const hasLogin = Boolean(config?.credential?.has_password);
  const needsLogin = changed && choice === SOLACTIVE && !hasLogin;

  const handleSave = async () => {
    if (!indexCode) return;
    if (needsLogin) {
      emitSourceChoose(indexCode, choice);
      return;
    }
    setSaving(true);
    try {
      const res: any = await callApi(`v1/index-datasource/by-index/${encodeURIComponent(indexCode)}`, "post", {
        data_source: choice,
      });
      applyConfig(res?.data?.config);
      emitSourceSaved(indexCode, res?.data?.config?.data_source);
      showNotification("Data source saved", "success");
    } catch (error: any) {
      showNotification(error?.message || "Could not save the data source", "error");
    } finally {
      setSaving(false);
    }
  };

  const wrap = "flex flex-wrap items-center gap-3 pt-3 mt-3 border-t border-dashed border-slate-200 dark:border-zink-500";

  if (!indexCode) {
    return (
      <div className={wrap}>
        <span className="text-slate-500">{`Save the ${returnType} ticker to set its data source.`}</span>
      </div>
    );
  }
  if (loading) {
    return (
      <div className={wrap}>
        <LoaderComponent />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className={wrap}>
        <span className="text-slate-500">{loadError}</span>
      </div>
    );
  }
  if (!config) return null;

  return (
    <div className={wrap}>
      <label htmlFor={`source-${indexCode}`} className="font-medium">
        Data source
      </label>
      <select
        id={`source-${indexCode}`}
        className="w-56 form-select"
        value={choice}
        disabled={saving}
        onChange={(e) => setChoice(e.target.value)}
      >
        {sources.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
        {!sources.some((s) => s.value === choice) && <option value={choice}>{choice}</option>}
      </select>
      {changed && (
        <>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600"
          >
            {saving ? <LoaderComponent /> : needsLogin ? "Next: add login" : "Save"}
          </button>
          <button type="button" className="text-slate-500 hover:underline" onClick={() => setChoice(savedSource)}>
            Cancel
          </button>
        </>
      )}
      {config.unrecognised && (
        <span className="text-xs text-red-500">{`"${config.data_source}" is not a source the sync understands; it falls back to ICAL3. Pick one and save.`}</span>
      )}
      {needsLogin && (
        <span className="w-full text-xs text-slate-500">
          Solactive needs an SFTP login. You add it once in the Verify source files card below.
        </span>
      )}
      {!changed && savedSource === SOLACTIVE && (
        <button type="button" className="text-custom-500 hover:underline" onClick={() => requestVerify(indexCode)}>
          Verify files →
        </button>
      )}
    </div>
  );
};

export default IndexDataSource;
